import moment from "moment";
import React, {
  FormEvent,
  FunctionComponent,
  useEffect,
  useState,
} from "react";
import styled from "styled-components";
import ActionText from "../components/ActionText";
import Row from "../components/Row";
import { styles } from "../styles/themes/style";
import { useConnectionsHandlers } from "../services/actions/connections/useConnectionsHandlers";
import TooltipWrapper from "../components/TooltipWrapper";

interface IConnectionFilterLabelsProps {
  searchValues?: any[];
  connectionsFilter?: any;
  onRemoveLabel?: (key: string) => void;
  resetFiltersHandler?: () => void;
  saveFiltersHandler?: () => void;
}

interface IToggleOperatorProps {
  isActive?: boolean;
}

const ConnectionFilterLabels: FunctionComponent<
  IConnectionFilterLabelsProps
> = ({
  searchValues,
  connectionsFilter,
  onRemoveLabel,
  resetFiltersHandler,
  saveFiltersHandler,
}) => {
  const [labels, setLabels] = useState<
    {
      key: string;
      value: string;
      tooltip?: string;
      uid?: string;
      operator?: string;
    }[]
  >([]);
  const { setScopedConnectionsFilterHandler: setConnectionsFilterHandler } = useConnectionsHandlers();

  const conversationStatusOptions = [
    { value: "all", label: "All conversations" },
    { value: "conversationsStarted", label: "Conversation started" },
    { value: "lastMessageOutgoing", label: "Last message outgoing" },
    { value: "lastMessageIncoming", label: "Last message incoming" },
    { value: "noConversationRecorded", label: "No conversation recorded" },
  ];

  useEffect(() => {
    if (connectionsFilter) {
      const newLabels: {
        key: string;
        value: string;
        tooltip?: string;
        uid?: string;
        operator?: string;
        groupId?: string;
      }[] = [];

      connectionsFilter.searchValues.forEach((searchObject: any) => {
        if (searchObject.searchValue) {
          const searchCriteria = [
            searchObject.searchByName === "yes" ? "Name" : "",
            searchObject.searchByHeadline === "yes" ? "Headline" : "",
            searchObject.searchByJobTitle === "yes" ? "Job Title" : "",
            searchObject.searchByCompany === "yes" ? "Company" : "",
            searchObject.searchByLocation === "yes" ? "Location" : "",
            searchObject.searchByNote === "yes" ? "Note" : "",
          ].filter(Boolean);

          const tooltip = `${searchCriteria.join(", ")} : ${
            searchObject.searchValue
          }`;

          newLabels.push({
            uid: searchObject.uid,
            key: "searchValue",
            value: `Search: ${searchObject.searchValue}`,
            tooltip: tooltip,
            operator:
              newLabels?.length && searchObject?.isOutterValueSearchOr
                ? searchObject?.isOutterValueSearchOr === "yes"
                  ? "OR"
                  : "AND"
                : undefined,
          });
        }
      });

      if (
        connectionsFilter.connectedAtFrom !== undefined &&
        connectionsFilter.connectedAtFrom !== ""
      ) {
        let dateConnected = `Date Connected: ${moment(
          connectionsFilter.connectedAtFrom
        ).format("MMM D, YYYY")}`;
        if (
          connectionsFilter.connectedAtTo !== undefined &&
          connectionsFilter.connectedAtTo !== ""
        ) {
          dateConnected += ` - ${moment(connectionsFilter.connectedAtTo).format(
            "MMM D, YYYY"
          )}`;
        }
        newLabels.push({
          key: "connectedAt",
          value: dateConnected,
          tooltip: dateConnected,
        });
      } else if (
        connectionsFilter.connectedAtTo !== undefined &&
        connectionsFilter.connectedAtTo !== ""
      ) {
        newLabels.push({
          key: "connectedAt",
          value: `Date Connected: - ${moment(
            connectionsFilter.connectedAtTo
          ).format("MMM D, YYYY")}`,
          tooltip: `Date Connected: - ${moment(
            connectionsFilter.connectedAtTo
          ).format("MMM D, YYYY")}`,
        });
      }

      if (connectionsFilter.selectedUsersMutual?.length) {
        if (connectionsFilter.selectedUsersMutual?.length === 1) {
          newLabels.push({
            key: "selectedUsersMutual",
            value: `Connected members: ${connectionsFilter.selectedUsersMutual[0].firstName} ${connectionsFilter.selectedUsersMutual[0].lastName}`,
            tooltip: `Connected members: ${connectionsFilter.selectedUsersMutual[0].firstName} ${connectionsFilter.selectedUsersMutual[0].lastName}`,
          });
        } else {
          let tooltip = `Connected members: `;
          tooltip += connectionsFilter.selectedUsersMutual
            .map((user: any) => `${user.firstName} ${user.lastName}`)
            .join(", ");
          newLabels.push({
            key: "selectedUsersMutual",
            value: `Connected members: ${connectionsFilter.selectedUsersMutual.length} Selected`,
            tooltip: tooltip,
          });
        }
      }

      // Company headcount (employees) range
      if (
        connectionsFilter.companyEmployeesMin !== undefined &&
        connectionsFilter.companyEmployeesMin !== ""
      ) {
        let text = `Company headcount: ${connectionsFilter.companyEmployeesMin}`;
        if (
          connectionsFilter.companyEmployeesMax !== undefined &&
          connectionsFilter.companyEmployeesMax !== ""
        ) {
          text += ` - ${connectionsFilter.companyEmployeesMax}`;
        }
        newLabels.push({
          key: "companyEmployees",
          value: text,
          tooltip: text,
        });
      } else if (
        connectionsFilter.companyEmployeesMax !== undefined &&
        connectionsFilter.companyEmployeesMax !== ""
      ) {
        const text = `Company headcount: - ${connectionsFilter.companyEmployeesMax}`;
        newLabels.push({
          key: "companyEmployees",
          value: text,
          tooltip: text,
        });
      }

      if (
        connectionsFilter.lastMessagedFrom !== undefined &&
        connectionsFilter.lastMessagedFrom !== ""
      ) {
        let dateLastMessaged = `Last Messaged: ${moment(
          connectionsFilter.lastMessagedFrom
        ).format("MMM D, YYYY")}`;
        if (
          connectionsFilter.lastMessagedTo !== undefined &&
          connectionsFilter.lastMessagedTo !== ""
        ) {
          dateLastMessaged += ` - ${moment(
            connectionsFilter.lastMessagedTo
          ).format("MMM D, YYYY")}`;
        }
        let tooltip = dateLastMessaged;
        if (connectionsFilter.conversationStatus !== undefined) {
          tooltip +=
            ", Conversation status: " +
            conversationStatusOptions?.find(
              (option: any) =>
                option?.value === connectionsFilter?.conversationStatus
            )?.label;
        }
        if (connectionsFilter.conversationOwners !== undefined) {
          if (
            connectionsFilter.conversationOwners.some(
              (item: any) => item.id === "all"
            )
          ) {
            tooltip += ", All members";
          } else if (connectionsFilter.conversationOwners?.length > 2) {
            tooltip += `, ${connectionsFilter.conversationOwners?.length} members selected`;
          } else {
            tooltip +=
              ", Conversation owners: " +
              connectionsFilter.conversationOwners
                .map((user: any) => user?.value)
                .join(", ");
          }
        }
        if (connectionsFilter.onlyLast === true) {
          tooltip += ", Show last messaged only";
        }
        newLabels.push({
          key: "lastMessaged",
          value: dateLastMessaged,
          tooltip: tooltip,
        });
      } else if (
        connectionsFilter.lastMessagedTo !== undefined &&
        connectionsFilter.lastMessagedTo !== ""
      ) {
        let tooltip = `Last Messaged: - ${moment(
          connectionsFilter.connectedAtTo
        ).format("MMM D, YYYY")}`;
        if (connectionsFilter.conversationStatus !== undefined) {
          tooltip +=
            ", Conversation status: " +
            conversationStatusOptions?.find(
              (option: any) =>
                option?.value === connectionsFilter?.conversationStatus
            )?.label;
        }
        if (connectionsFilter.conversationOwners !== undefined) {
          if (
            connectionsFilter.conversationOwners.some(
              (item: any) => item.id === "all"
            )
          ) {
            tooltip += ", All members";
          } else if (connectionsFilter.conversationOwners?.length > 2) {
            tooltip += `, ${connectionsFilter.conversationOwners?.length} members selected`;
          } else {
            tooltip +=
              ", Conversation owners: " +
              connectionsFilter.conversationOwners
                .map((user: any) => user?.value)
                .join(", ");
          }
        }
        if (connectionsFilter.onlyLast === true) {
          tooltip += ", Show last messaged only";
        }
        newLabels.push({
          key: "lastMessaged",
          value: `Last Messaged: - ${moment(
            connectionsFilter.connectedAtTo
          ).format("MMM D, YYYY")}`,
          tooltip: tooltip,
        });
      } else if (
        connectionsFilter.conversationStatus !== undefined &&
        connectionsFilter.conversationStatus !== ""
      ) {
        let tooltip = `Last Messaged: `;
        let value = `Last Messaged: `;
        if (connectionsFilter.conversationStatus !== undefined) {
          tooltip +=
            "Conversation status: " +
            conversationStatusOptions?.find(
              (option: any) =>
                option?.value === connectionsFilter?.conversationStatus
            )?.label;
          tooltip += ", ";
          value += conversationStatusOptions?.find(
            (option: any) =>
              option?.value === connectionsFilter?.conversationStatus
          )?.label;
          value += ", ";
        }
        if (connectionsFilter.conversationOwners !== undefined) {
          if (
            connectionsFilter.conversationOwners.some(
              (item: any) => item.id === "all"
            )
          ) {
            value += "All members";
            tooltip += "All members";
          } else if (connectionsFilter.conversationOwners?.length > 2) {
            value += `${connectionsFilter.conversationOwners?.length} members selected`;
            tooltip += `${connectionsFilter.conversationOwners?.length} members selected`;
          } else {
            value += connectionsFilter.conversationOwners
              .map((user: any) => user?.value)
              .join(", ");
            tooltip +=
              "Conversation owners: " +
              connectionsFilter.conversationOwners
                .map((user: any) => user?.value)
                .join(", ");
          }
        }
        if (connectionsFilter.onlyLast === true) {
          tooltip += ", Show last messaged only";
        }
        newLabels.push({
          key: "lastMessaged",
          value: value,
          tooltip: tooltip,
        });
      } else if (connectionsFilter.conversationOwners !== undefined) {
        let tooltip = `Last Messaged: `;
        let value = `Last Messaged: `;
        if (
          connectionsFilter.conversationOwners.some(
            (item: any) => item.id === "all"
          )
        ) {
          value += "All members";
          tooltip += "All members";
        } else if (connectionsFilter.conversationOwners?.length > 2) {
          value += `${connectionsFilter.conversationOwners?.length} members selected`;
          tooltip += `${connectionsFilter.conversationOwners?.length} members selected`;
        } else {
          value += connectionsFilter.conversationOwners
            .map((user: any) => user?.value)
            .join(", ");
          tooltip +=
            "Conversation owners: " +
            connectionsFilter.conversationOwners
              .map((user: any) => user?.value)
              .join(", ");
        }
        if (connectionsFilter.onlyLast === true) {
          tooltip += ", Show last messaged only";
        }

        newLabels.push({
          key: "lastMessaged",
          value: value,
          tooltip: tooltip,
        });
      } else if (connectionsFilter.onlyLast === true) {
        newLabels.push({
          key: "lastMessaged",
          value: "Show last messaged only",
          tooltip: "Show last messaged only",
        });
      }

      if (connectionsFilter.hiddenConnectionsValue?.value !== "no") {
        newLabels.push({
          key: "hiddenConnections",
          value: `Hidden Contacts: ${connectionsFilter.hiddenConnectionsValue?.label}`,
          tooltip: `Hidden Contacts: ${connectionsFilter.hiddenConnectionsValue?.label}`,
        });
      }

      if (
        connectionsFilter.birthDateFrom !== undefined &&
        connectionsFilter.birthDateFrom !== ""
      ) {
        let dateConnected = `Date Of Birth: ${moment(
          connectionsFilter.birthDateFrom
        ).format("MMM D, YYYY")}`;
        if (
          connectionsFilter.birthDateTo !== undefined &&
          connectionsFilter.birthDateTo !== ""
        ) {
          dateConnected += ` - ${moment(connectionsFilter.birthDateTo).format(
            "MMM D, YYYY"
          )}`;
        }
        newLabels.push({
          key: "birthDate",
          value: dateConnected,
          tooltip: dateConnected,
        });
      } else if (
        connectionsFilter.birthDateTo !== undefined &&
        connectionsFilter.birthDateTo !== ""
      ) {
        newLabels.push({
          key: "birthDate",
          value: `Date Connected: - ${moment(
            connectionsFilter.birthDateTo
          ).format("MMM D, YYYY")}`,
          tooltip: `Date Connected: - ${moment(
            connectionsFilter.birthDateTo
          ).format("MMM D, YYYY")}`,
        });
      }

      if (connectionsFilter.selectedLanguages?.length) {
        if (connectionsFilter.selectedLanguages?.length === 1) {
          newLabels.push({
            key: "selectedLanguages",
            value: `Language: ${connectionsFilter.selectedLanguages[0]}`,
          });
        } else {
          let tooltip = `Languages, ${connectionsFilter.languageCondition}: `;
          tooltip += connectionsFilter.selectedLanguages
            .slice(0, 3)
            .map((lang: any) => lang)
            .join(", ");
          if (connectionsFilter.selectedLanguages.length > 3) {
            tooltip += ", ...";
          }
          newLabels.push({
            key: "selectedLanguages",
            value: `Language: ${connectionsFilter.selectedLanguages.length} Selected`,
            tooltip: tooltip,
          });
        }
      }
      if (
        connectionsFilter?.contactInfo?.value &&
        connectionsFilter.contactInfo?.value !== "all"
      ) {
        newLabels.push({
          key: "contactInfo",
          value: `Li Email: ${connectionsFilter.contactInfo?.label}`,
          tooltip: `Li Email: ${connectionsFilter.contactInfo?.label}`,
        });
      }
      if (
        connectionsFilter?.liPhone?.value &&
        connectionsFilter.liPhone?.value !== "all"
      ) {
        newLabels.push({
          key: "liPhone",
          value: `Li Phone: ${connectionsFilter.liPhone?.label}`,
          tooltip: `Li Phone: ${connectionsFilter.liPhone?.label}`,
        });
      }
      if (
        connectionsFilter.privateEmail?.value &&
        connectionsFilter.privateEmail?.value !== "all"
      ) {
        newLabels.push({
          key: "privateEmail",
          value: `Personal Email: ${connectionsFilter.privateEmail?.label}`,
          tooltip: `Personal Email: ${connectionsFilter.privateEmail?.label}`,
        });
      }
      if (
        connectionsFilter.businessEmail?.value &&
        connectionsFilter.businessEmail?.value !== "all"
      ) {
        newLabels.push({
          key: "businessEmail",
          value: `Business Email: ${connectionsFilter.businessEmail?.label}`,
          tooltip: `Business Email: ${connectionsFilter.businessEmail?.label}`,
        });
      }
      if (
        connectionsFilter.phone?.value &&
        connectionsFilter.phone?.value !== "all"
      ) {
        newLabels.push({
          key: "phone",
          value: `Phone: ${connectionsFilter.phone?.label}`,
          tooltip: `Phone: ${connectionsFilter.phone?.label}`,
        });
      }
      if (
        connectionsFilter?.industry?.value &&
        connectionsFilter.industry?.value !== ""
      ) {
        newLabels.push({
          key: "industry",
          value: `Industry: ${connectionsFilter.industry?.label}`,
          tooltip: `Industry: ${connectionsFilter.industry?.label}`,
        });
      }

      if (
        connectionsFilter?.searchByFollowers !== undefined &&
        connectionsFilter?.searchByFollowers !== ""
      ) {
        let searchByFollowers = `Number of Followers: ${connectionsFilter.searchByFollowers}`;
        if (connectionsFilter.searchByMaxFollowers !== undefined) {
          searchByFollowers += ` - ${connectionsFilter.searchByMaxFollowers}`;
        }
        newLabels.push({
          key: "searchByFollowers",
          value: searchByFollowers,
          tooltip: searchByFollowers,
        });
      } else if (
        connectionsFilter.searchByMaxFollowers !== undefined &&
        connectionsFilter?.searchByMaxFollowers !== ""
      ) {
        newLabels.push({
          key: "searchByFollowers",
          value: `Number of Followers: 0 - ${connectionsFilter.searchByMaxFollowers}`,
          tooltip: `Number of Followers: 0 - ${connectionsFilter.searchByMaxFollowers}`,
        });
      }

      if (connectionsFilter.selectedTags?.length) {
        if (connectionsFilter.selectedTags?.length === 1) {
          newLabels.push({
            key: "selectedTags",
            value: `Tags: ${connectionsFilter.selectedTags[0]?.title}`,
          });
        } else {
          let tooltip = `Tags, ${connectionsFilter.tagCondition}: `;
          tooltip += connectionsFilter.selectedTags
            .slice(0, 3)
            .map((tag: any) => tag.title)
            .join(", ");
          if (connectionsFilter.selectedTags.length > 3) {
            tooltip += ", ...";
          }
          newLabels.push({
            key: "selectedTags",
            value: `Tags: ${connectionsFilter.selectedTags.length} Selected`,
            tooltip: tooltip,
          });
        }
      }

      if (connectionsFilter.noTags) {
        newLabels.push({
          key: "selectedTags",
          value: `Without Tags`,
          tooltip: `Without Tags`,
        });
      }

      if (connectionsFilter.selectedExcludeTags?.length) {
        if (connectionsFilter.selectedExcludeTags?.length === 1) {
          newLabels.push({
            key: "selectedExcludeTags",
            value: `Exclude Tags: ${connectionsFilter.selectedExcludeTags[0]?.title}`,
          });
        } else {
          let tooltip = `Exclude Tags, ${connectionsFilter.tagCondition}: `;
          tooltip += connectionsFilter.selectedExcludeTags
            .slice(0, 3)
            .map((tag: any) => tag.title)
            .join(", ");
          if (connectionsFilter.selectedExcludeTags.length > 3) {
            tooltip += ", ...";
          }
          newLabels.push({
            key: "selectedExcludeTags",
            value: `Exclude Tags: ${connectionsFilter.selectedExcludeTags.length} Selected`,
            tooltip: tooltip,
          });
        }
      }

      if (
        connectionsFilter.note !== "all" &&
        connectionsFilter.note?.value !== "all"
      ) {
        newLabels.push({
          key: "note",
          value: `Note: ${connectionsFilter.note?.label}`,
          tooltip: `Note: ${connectionsFilter.note?.label}`,
        });
      }

      if (connectionsFilter.autotagsFilter !== undefined) {
        let tooltip = `Autotags: `;
        let value = `Autotags: `;
        if (connectionsFilter.autotagsFilter?.length > 2) {
          value += `${connectionsFilter.autotagsFilter?.length} selected`;
          tooltip += connectionsFilter.autotagsFilter
            .map((tag: any) =>
              tag._id === "LD Removed" || tag._id === "Li Removed"
                ? tag._id
                : tag.title
            )
            .join(", ");
        } else {
          value += connectionsFilter.autotagsFilter
            .map((tag: any) =>
              tag._id === "LD Removed" || tag._id === "Li Removed"
                ? tag._id
                : tag.title
            )
            .join(", ");
          tooltip += connectionsFilter.autotagsFilter
            .map((tag: any) =>
              tag._id === "LD Removed" || tag._id === "Li Removed"
                ? tag._id
                : tag.title
            )
            .join(", ");
        }

        newLabels.push({
          key: "autotags",
          value: value,
          tooltip: tooltip,
        });
      }

      if (connectionsFilter.hasJobChanges && connectionsFilter.hasJobChanges === 'yes') {
        newLabels.push({
          key: "hasJobChanges",
          value: "Job Changes",
          tooltip: "Job Changes",
        });
      }

      if (connectionsFilter.hasCompanyChanges && connectionsFilter.hasCompanyChanges === 'yes') {
        newLabels.push({
          key: "hasCompanyChanges",
          value: "Company Changes",
          tooltip: "Company Changes",
        });
      }

      if (Array.isArray(connectionsFilter.connectionIdsFilter) && connectionsFilter.connectionIdsFilter.length) {
        const meta = connectionsFilter.connectionIdsFilterMeta;
        const isAiChat = meta?.source === 'ai-chat';
        const groups = Array.isArray((meta as any)?.groups) ? (meta as any).groups : [];
        if (isAiChat && groups.length) {
          groups.forEach((g: any) => {
            const fullTitle = g?.title ? `AI ${g.title}` : 'AI chat results';
            const truncatedTitle = fullTitle.length > 30 ? `${fullTitle.substring(0, 30)}...` : fullTitle;
            newLabels.push({
              key: "aiChatGroup",
              value: truncatedTitle,
              tooltip: fullTitle,
              groupId: g?.id,
            });
          });
        } else {
          const fullLabel = isAiChat 
            ? (meta?.title ? `AI ${meta.title}` : 'AI chat results')
            : 'Highlighted connections';
          const truncatedLabel = fullLabel.length > 35 ? `${fullLabel.substring(0, 35)}...` : fullLabel;
          newLabels.push({
            key: "connectionIdsFilter",
            value: truncatedLabel,
            tooltip: `${fullLabel} (${connectionsFilter.connectionIdsFilter.length})`,
          });
        }
      }

      setLabels(newLabels);
    }
  }, [connectionsFilter]);

  const handleToggleOperator = (newValue: string, uid?: string) => {
    const updatedFilter = {
      ...connectionsFilter,
      searchValues: connectionsFilter.searchValues.map((val: any) =>
        val.uid === uid ? { ...val, isOutterValueSearchOr: newValue } : val
      ),
      sortClicked: false,
    };
    setConnectionsFilterHandler(updatedFilter);
  };

  const handleRemoveLabel = (label: {
    key: string;
    value: string;
    uid?: string;
    groupId?: string;
  }) => {
    let updatedFilter;
    if (label.key === "searchValue") {
      updatedFilter = {
        ...connectionsFilter,
        searchValues: connectionsFilter.searchValues.filter(
          (val: any) => val.uid !== label.uid
        ),
        sortClicked: false,
      };
    } else if (label.key === "connectedAt") {
      updatedFilter = {
        ...connectionsFilter,
        connectedAtFrom: undefined,
        connectedAtTo: undefined,
        sortClicked: false,
      };
    } else if (label.key === "selectedUsersMutual") {
      updatedFilter = {
        ...connectionsFilter,
        selectedUsersMutual: [],
        sortClicked: false,
      };
    } else if (label.key === "lastMessaged") {
      updatedFilter = {
        ...connectionsFilter,
        lastMessagedFrom: undefined,
        lastMessagedTo: undefined,
        conversationOwners: undefined,
        conversationStatus: undefined,
        onlyLast: undefined,
        sortClicked: false,
      };
    } else if (label.key === "hiddenConnections") {
      updatedFilter = {
        ...connectionsFilter,
        hiddenConnectionsValue: { label: "No", value: "no" },
        sortClicked: false,
      };
    } else if (label.key === "birthDate") {
      updatedFilter = {
        ...connectionsFilter,
        birthDateFrom: undefined,
        birthDateTo: undefined,
        sortClicked: false,
      };
    } else if (label.key === "selectedLanguages") {
      updatedFilter = {
        ...connectionsFilter,
        selectedLanguages: [],
        sortClicked: false,
      };
    } else if (label.key === "contactInfo") {
      updatedFilter = {
        ...connectionsFilter,
        contactInfo: { label: "All", value: "all" },
        sortClicked: false,
      };
    } else if (label.key === "liPhone") {
      updatedFilter = {
        ...connectionsFilter,
        liPhone: { label: "All", value: "all" },
        sortClicked: false,
      };
    } else if (label.key === "privateEmail") {
      updatedFilter = {
        ...connectionsFilter,
        privateEmail: { label: "All", value: "all" },
        sortClicked: false,
      };
    } else if (label.key === "businessEmail") {
      updatedFilter = {
        ...connectionsFilter,
        businessEmail: { label: "All", value: "all" },
        sortClicked: false,
      };
    } else if (label.key === "phone") {
      updatedFilter = {
        ...connectionsFilter,
        phone: { label: "All", value: "all" },
        sortClicked: false,
      };
    } else if (label.key === "industry") {
      updatedFilter = {
        ...connectionsFilter,
        industry: { label: "All Industries", value: null },
        sortClicked: false,
      };
    } else if (label.key === "searchByFollowers") {
      updatedFilter = {
        ...connectionsFilter,
        searchByFollowers: undefined,
        searchByMaxFollowers: undefined,
        sortClicked: false,
      };
    } else if (label.key === "companyEmployees") {
      updatedFilter = {
        ...connectionsFilter,
        companyEmployeesMin: undefined,
        companyEmployeesMax: undefined,
        sortClicked: false,
      };
    } else if (label.key === "selectedTags") {
      updatedFilter = {
        ...connectionsFilter,
        selectedTags: [],
        noTags: false,
        sortClicked: false,
      };
    } else if (label.key === "selectedExcludeTags") {
      updatedFilter = {
        ...connectionsFilter,
        selectedExcludeTags: [],
        sortClicked: false,
      };
    } else if (label.key === "note") {
      updatedFilter = {
        ...connectionsFilter,
        note: { label: "All", value: "all" },
        sortClicked: false,
      };
    } else if (label.key === "autotags") {
      updatedFilter = {
        ...connectionsFilter,
        autotagsFilter: undefined,
        sortClicked: false,
      };
    } else if (label.key === "hasJobChanges") {
      updatedFilter = {
        ...connectionsFilter,
        hasJobChanges: undefined,
        sortClicked: false,
      };
    } else if (label.key === "hasCompanyChanges") {
      updatedFilter = {
        ...connectionsFilter,
        hasCompanyChanges: undefined,
        sortClicked: false,
      };
    } else if (label.key === "connectionIdsFilter") {
      updatedFilter = {
        ...connectionsFilter,
        connectionIdsFilter: undefined,
        connectionIdsFilterMeta: undefined,
        sortClicked: false,
      };
    } else if (label.key === "aiChatGroup") {
      const meta: any = connectionsFilter.connectionIdsFilterMeta;
      const groups: any[] = Array.isArray(meta?.groups) ? meta.groups : [];
      const remaining = groups.filter((g) => g?.id !== label.groupId);

      if (!remaining.length) {
        updatedFilter = {
          ...connectionsFilter,
          connectionIdsFilter: undefined,
          connectionIdsFilterMeta: undefined,
          sortClicked: false,
        };
      } else {
        const unionIds = Array.from(
          new Set(
            remaining
              .flatMap((g: any) => (Array.isArray(g?.connectionIds) ? g.connectionIds : []))
              .filter(Boolean)
          )
        );

        updatedFilter = {
          ...connectionsFilter,
          connectionIdsFilter: unionIds,
          connectionIdsFilterMeta: { ...(meta || {}), groups: remaining },
          sortClicked: false,
        };
      }
    }

    onRemoveLabel?.(label.key);
    setConnectionsFilterHandler(updatedFilter);
  };

  const saveSavedFiltersHandler = (e: FormEvent) => {
    e.preventDefault();
    saveFiltersHandler && saveFiltersHandler();
  };

  return labels?.length ? (
    <StyledForm>
      <Row gap="10px">
        <ActionText onClickHandler={(e) => saveSavedFiltersHandler(e)}>
          Save filters
        </ActionText>
        <ActionText
          danger
          onClickHandler={() => resetFiltersHandler && resetFiltersHandler()}
        >
          Clear all
        </ActionText>
      </Row>

      <Row gap="8px">
        {labels.map((label, index) => (
          <React.Fragment key={index}>
            {label?.operator && (
              <Label style={{ padding: "0px 4px" }}>
                <Option
                  isActive={label?.operator === "AND"}
                  onClick={() => handleToggleOperator("no", label?.uid)}
                >
                  And
                </Option>
                <Separator />
                <Option
                  isActive={label?.operator === "OR"}
                  onClick={() => handleToggleOperator("yes", label?.uid)}
                >
                  Or
                </Option>
              </Label>
            )}
            <TooltipWrapper tooltipText={label?.tooltip ?? ""}>
              <Label style={{ padding: "2px 4px" }}>
                {label?.value}
                <CloseButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveLabel(label);
                  }}
                  className="clear-svg"
                  width="12"
                  height="12"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.96 11.825 6 7.785l4.04 4.04a.6.6 0 0 0 .846 0l.94-.939a.6.6 0 0 0 0-.847L7.785 6l4.039-4.04a.6.6 0 0 0 0-.846l-.939-.939a.6.6 0 0 0-.847 0L6 4.215 1.96.175a.6.6 0 0 0-.846 0l-.939.939a.6.6 0 0 0 0 .847L4.215 6 .175 10.04a.6.6 0 0 0 0 .847l.94.938a.6.6 0 0 0 .846 0z"
                    fill="#8C96AD"
                    fillRule="evenodd"
                  />
                </CloseButton>
              </Label>
            </TooltipWrapper>
          </React.Fragment>
        ))}
      </Row>
    </StyledForm>
  ) : null;
};

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  padding: 12px;
  gap: 8px;
  width: 100%;
`;

const Label = styled.div`
  font-family: NunitoSans;
  font-size: 14px;
  background-color: ${styles.colors.primary100};
  border: 1px solid;
  border-color: ${styles.colors.primary600};
  color: ${styles.colors.primary600};
  border-radius: 4px;
  display: flex;
  align-items: center;
  max-width: 350px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CloseButton = styled.svg`
  background: none;
  border: none;
  margin-left: 8px;
  cursor: pointer;
  font-size: 12px;
  cursor: pointer;
  path {
    fill: ${styles.colors.primary600};
  }
  &:hover path {
    fill: ${styles.colors.primary500};
  }
`;

const Option = styled.div<IToggleOperatorProps>`
  padding: 2px 4px;
  font-weight: ${({ isActive }) => (isActive ? "bold" : "normal")};
  color: ${styles.colors.primary600};
  cursor: pointer;
`;

const Separator = styled.div`
  width: 1px;
  height: 100%;
  background-color: ${styles.colors.primary600};
`;

export default ConnectionFilterLabels;
